import {LatLngDto} from '../../../../shared/dtos/estimate-drag-path.dto';

/*
 * Pure map math for the job site map (SQ-621): the port of Android's
 * core/geo/MapGeometry.kt, constant for constant, so both clients store the
 * same rear anchors, turret points and drag distances for the same gesture.
 * Free of any Maps SDK type, like Android's, so it runs under Jest.
 */

/** Matches google.maps.geometry.spherical and Android's SphericalUtil. */
const EARTH_RADIUS_METERS = 6_371_009;
const METERS_IN_FOOT = 0.3048;
/** Flat-earth constant of the spawn and footprint helpers (Android and the old web map). */
const METERS_PER_DEGREE_LAT = 111_320;
const EARTH_CIRCUMFERENCE_METERS = 40_075_016.686;

const rad = (deg: number) => deg * Math.PI / 180;
const deg = (r: number) => r * 180 / Math.PI;
const norm360 = (d: number) => ((d % 360) + 360) % 360;

/** An endpoint dropped within this distance of ANOTHER truck moves the route to it. */
export const CHIPPER_SNAP_METERS = 5;

// ---- The truck & chipper: true scale, 45 ft nose to the back of the chipper ----------------

export const CHIPPER_LENGTH_FEET = 45;
export const CHIPPER_LENGTH_METERS = CHIPPER_LENGTH_FEET * METERS_IN_FOOT;
/** Width / length of truck_chipper_icon.png (80 x 289, drawn front up). */
export const CHIPPER_ASPECT = 80 / 289;
/** Wheel-to-wheel body width as a share of the asset's width: the half moon's tips line up with it. */
export const CHIPPER_BODY_WIDTH_FRACTION = 66 / 80;
/** The truck's bed (where its number sits) from the REAR of the chipper. */
export const CHIPPER_BED_CENTER_FRACTION_FROM_REAR = 0.595;

// ---- The crane (SQ-527): crane_icon.png is 640 x 174, cab to the right ----------------------

export const CRANE_LENGTH_FEET = 45;
export const CRANE_LENGTH_METERS = CRANE_LENGTH_FEET * METERS_IN_FOOT;
const CRANE_ART_LENGTH_PX = 640;
const CRANE_ART_WIDTH_PX = 174;
const CRANE_REAR_PX = 2;
const CRANE_BUMPER_PX = 596;
/** The turret, from the art's rear edge: just behind the operator cab's window. */
const CRANE_TURRET_PX = 128;
/** Drawn artwork length (the boom's overhang included) over the 45 ft truck. */
export const CRANE_ART_PER_TRUCK = CRANE_ART_LENGTH_PX / (CRANE_BUMPER_PX - CRANE_REAR_PX);
export const CRANE_ART_ASPECT = CRANE_ART_WIDTH_PX / CRANE_ART_LENGTH_PX;
/** The turret's place along the artwork, rear edge 0 to boom tip 1. */
export const CRANE_TURRET_FRACTION = CRANE_TURRET_PX / CRANE_ART_LENGTH_PX;
/** The truck's rear centre to the turret, on the ground: a crane turns about its rear. */
export const CRANE_REAR_TO_TURRET_METERS =
  CRANE_LENGTH_METERS * (CRANE_TURRET_PX - CRANE_REAR_PX) / (CRANE_BUMPER_PX - CRANE_REAR_PX);
/** Where the crane's number sits along the artwork: on the bed. */
export const CRANE_NUMBER_FRACTION = 0.45;

// ---- Staging piles (SQ-527) ------------------------------------------------------------------

export const PILE_OFFSET_FRACTION = 0.15;
export const PILE_OFFSET_MIN_METERS = 5;
export const PILE_OFFSET_MAX_METERS = 7.5;

// ---- Gates ---------------------------------------------------------------------------------------

/** The gate's icon, a fixed screen size (SQ-286: three quarters of the first 52 x 35). */
export const GATE_ICON_WIDTH_DP = 39;
export const GATE_ICON_HEIGHT_DP = 26.25;

// ---- Spherical math --------------------------------------------------------------------------

/** google.maps.geometry.spherical.computeOffset, on the same earth. */
export function computeOffset(from: LatLngDto, distanceMeters: number, headingDeg: number): LatLngDto {
  const d = distanceMeters / EARTH_RADIUS_METERS;
  const heading = rad(headingDeg);
  const fromLat = rad(from.lat);
  const fromLng = rad(from.lng);
  const cosD = Math.cos(d);
  const sinD = Math.sin(d);
  const sinLat = Math.sin(fromLat);
  const cosLat = Math.cos(fromLat);
  const sinToLat = cosD * sinLat + sinD * cosLat * Math.cos(heading);
  const toLat = Math.asin(sinToLat);
  const toLng = fromLng + Math.atan2(sinD * cosLat * Math.sin(heading), cosD - sinLat * sinToLat);
  return {lat: deg(toLat), lng: deg(toLng)};
}

/** Great-circle distance in meters (the asin form: stable at job-site distances). */
export function distanceMeters(from: LatLngDto, to: LatLngDto): number {
  const dLat = rad(to.lat - from.lat);
  const dLng = rad(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(from.lat)) * Math.cos(rad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Compass bearing from one point to another, degrees clockwise from north. */
export function bearingDeg(from: LatLngDto, to: LatLngDto): number {
  const lat1 = rad(from.lat);
  const lat2 = rad(to.lat);
  const dLng = rad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return norm360(deg(Math.atan2(y, x)));
}

export const metersToFeet = (meters: number) => meters / METERS_IN_FOOT;

/** A route's length in feet: what the work item's dragDistanceFeet holds (an AI pricing input). */
export function pathDistanceFeet(points: LatLngDto[]): number {
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) total += metersToFeet(distanceMeters(points[i], points[i + 1]));
  return total;
}

/** Feet and inches to the nearest inch: "38 ft 4 in", "9 in", "12 ft". */
export function feetInches(meters: number): string {
  const totalInches = Math.round(metersToFeet(meters) * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  if (feet === 0) return `${inches} in`;
  if (inches === 0) return `${feet} ft`;
  return `${feet} ft ${inches} in`;
}

/** Ground meters one CSS pixel covers at a zoom (Web Mercator: the JS map's world is 256 px at zoom 0, like Android's dp). */
export const metersPerPx = (lat: number, zoom: number) =>
  EARTH_CIRCUMFERENCE_METERS * Math.cos(rad(lat)) / Math.pow(2, zoom + 8);

// ---- The truck & chipper's pose ----------------------------------------------------------------

/** The back centre of the chipper: where drag routes end and what a turn pivots on. */
export const chipperRearCenter = (center: LatLngDto, headingDeg: number): LatLngDto =>
  computeOffset(center, CHIPPER_LENGTH_METERS / 2, (headingDeg + 180) % 360);

/** The stored centre for a planted rear. */
export const chipperCenterFromRear = (rear: LatLngDto, headingDeg: number): LatLngDto =>
  computeOffset(rear, CHIPPER_LENGTH_METERS / 2, headingDeg % 360);

// ---- The crane's pose -----------------------------------------------------------------------------

export const craneRear = (turret: LatLngDto, headingDeg: number): LatLngDto =>
  computeOffset(turret, CRANE_REAR_TO_TURRET_METERS, (headingDeg + 180) % 360);

export const craneTurretFromRear = (rear: LatLngDto, headingDeg: number): LatLngDto =>
  computeOffset(rear, CRANE_REAR_TO_TURRET_METERS, norm360(headingDeg));

/** Where a staging pile starts: off the work item's tip, square to the reach, on the crane's right. */
export function defaultPileSpot(turret: LatLngDto, tip: LatLngDto): LatLngDto {
  const reach = distanceMeters(turret, tip);
  const offset = Math.min(PILE_OFFSET_MAX_METERS, Math.max(PILE_OFFSET_MIN_METERS, reach * PILE_OFFSET_FRACTION));
  return computeOffset(tip, offset, (bearingDeg(turret, tip) + 90) % 360);
}

// ---- Drag routes ----------------------------------------------------------------------------------

export interface DragPathUpdate {
  points: LatLngDto[];
  distanceFeet: number;
  /** Set only when the moved point snapped to a different truck. */
  chipperTruckId?: number;
}

export interface Chipper {
  id: number;
  lat: number;
  lng: number;
  heading?: number | null;
}

/**
 * A route after one of its points moved. Dropped within CHIPPER_SNAP_METERS
 * of a truck other than its own (measured to the truck's stored centre), the
 * route is cut at that point and re-ends at the new truck's rear.
 */
export function dragPathUpdate(points: LatLngDto[], changedPointIndex: number, currentChipperTruckId: number | undefined, chippers: Chipper[]): DragPathUpdate {
  const changed = points[changedPointIndex];
  if (!changed) return {points, distanceFeet: pathDistanceFeet(points)};
  let nearest: {chipper: Chipper; meters: number} | undefined;
  chippers
    .filter(c => c.id !== currentChipperTruckId)
    .forEach(c => {
      const meters = distanceMeters({lat: c.lat, lng: c.lng}, changed);
      if (!nearest || meters < nearest.meters) nearest = {chipper: c, meters};
    });
  if (nearest && nearest.meters < CHIPPER_SNAP_METERS) {
    const c = nearest.chipper;
    const snapped = [...points.slice(0, changedPointIndex), chipperRearCenter({lat: c.lat, lng: c.lng}, c.heading ?? 0)];
    return {points: snapped, distanceFeet: pathDistanceFeet(snapped), chipperTruckId: c.id};
  }
  return {points, distanceFeet: pathDistanceFeet(points)};
}

// ---- Gates ----------------------------------------------------------------------------------------

/**
 * Liang-Barsky clip of the segment against [-hw, hw] x [-hh, hh]: true when
 * any part of it is inside.
 */
function segmentTouchesBox(x0: number, y0: number, x1: number, y1: number, hw: number, hh: number): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  let t0 = 0;
  let t1 = 1;
  const p = [-dx, dx, -dy, dy];
  const q = [x0 + hw, hw - x0, y0 + hh, hh - y0];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return false;
    } else {
      const r = q[i] / p[i];
      if (p[i] < 0) {
        if (r > t1) return false;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return false;
        if (r < t1) t1 = r;
      }
    }
  }
  return true;
}

/**
 * True when a route touches the gate's icon at the zoom the person sees: the
 * icon is a fixed screen size, so its size on the ground follows the zoom,
 * turned headingDeg clockwise from north.
 */
export function pathTouchesGate(points: LatLngDto[], center: LatLngDto, zoom: number, headingDeg = 0): boolean {
  if (points.length === 0) return false;
  const mpp = metersPerPx(center.lat, zoom);
  const halfWidth = GATE_ICON_WIDTH_DP / 2 * mpp;
  const halfHeight = GATE_ICON_HEIGHT_DP / 2 * mpp;
  const cosLat = Math.cos(rad(center.lat));
  const h = rad(headingDeg);
  const cosH = Math.cos(h);
  const sinH = Math.sin(h);
  const local = (p: LatLngDto): [number, number] => {
    const x = (p.lng - center.lng) * METERS_PER_DEGREE_LAT * cosLat;
    const y = (p.lat - center.lat) * METERS_PER_DEGREE_LAT;
    return [x * cosH - y * sinH, x * sinH + y * cosH];
  };
  const locals = points.map(local);
  if (locals.length === 1) return Math.abs(locals[0][0]) <= halfWidth && Math.abs(locals[0][1]) <= halfHeight;
  for (let i = 0; i < locals.length - 1; i++) {
    const [ax, ay] = locals[i];
    const [bx, by] = locals[i + 1];
    if (segmentTouchesBox(ax, ay, bx, by, halfWidth, halfHeight)) return true;
  }
  return false;
}
